import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PreviousLessonReports from "./PreviousLessonReports";
import HomeworkInput from "./HomeworkInput";
import {
  FaBook,
  FaTasks,
  FaClipboardList,
  FaCheck,
  FaTimesCircle,
  FaUserGraduate,
  FaChartLine,
  FaChalkboardTeacher,
  FaRegStickyNote,
  FaCalendarAlt,
  FaSpinner,
} from "react-icons/fa";
import { getRequest, postRequest, putRequest } from "../../apiWrapper";
import { formatDate } from "../../utils/formatDate"; // ★追加
import AddStudentGoalModal from "./AddStudentGoalModal";

const LessonReportForm = ({
  studentid,
  teacherid,
  studentName,
  studentColor,
  onSave,
  initialData,
  isAdminEditing = false,
}) => {
  // schoolidはサーバー側でJWTから取得するため、クライアント側では保持不要
  const [subjects, setSubjects] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]); // 全科目情報
  const [formData, setFormData] = useState(initialData || {});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false); // 保存中状態
  const [errors, setErrors] = useState({});
  const inputRefs = useRef({});
  const [refreshReports, setRefreshReports] = useState(false); // 更新フラグ
  const [dataLoaded, setDataLoaded] = useState({}); // 各生徒ごとの復元状態
  const [selectedDate, setSelectedDate] = useState(""); // 日付状態
  const [previousReports, setPreviousReports] = useState([]); // 過去3件の報告
  const [showSuggestions, setShowSuggestions] = useState({
    unit: false,
    material: false,
    homework: false,
    nextlesson: false,
  });
  const [isCreatingAI, setIsCreatingAI] = useState(false);
  const [studentGoal, setStudentGoal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 単元候補モーダル
  const [isUnitsModalOpen, setIsUnitsModalOpen] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]); // { id, unit_name }[]

  // スケジュールモーダル用 state
  const [teachers, setTeachers] = useState([]);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleItems, setScheduleItems] = useState([]);

  // 学年の選択肢（数値と表示ラベル）
  const gradeOptions = [
    { value: 1, label: "小学校1年" },
    { value: 2, label: "小学校2年" },
    { value: 3, label: "小学校3年" },
    { value: 4, label: "小学校4年" },
    { value: 5, label: "小学校5年" },
    { value: 6, label: "小学校6年" },
    { value: 7, label: "中学校1年" },
    { value: 8, label: "中学校2年" },
    { value: 9, label: "中学校3年" },
    { value: 10, label: "高校1年" },
    { value: 11, label: "高校2年" },
    { value: 12, label: "高校3年" },
    { value: 13, label: "既卒" }
  ];

  // 数値の学年をラベルに変換する関数
  const getGradeLabel = (grade) => {
    const option = gradeOptions.find((opt) => Number(opt.value) === Number(grade));
    return option ? option.label : "-";
  };

  // 講師情報をAPI経由で取得（担当外のコマの講師名表示用）
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const res = await getRequest("teachers?excludeRetired=true");
        if (res && res.teachers) {
          setTeachers(res.teachers);
        }
      } catch (error) {
        console.error("❌ 講師情報の取得エラー:", error);
      }
    };
    fetchTeachers();
  }, []);


  // 全科目情報をAPI経由で取得
  useEffect(() => {
    const fetchAllSubjects = async () => {
      try {
        const data = await getRequest("subjects");
        if (data && data.subjects) {
          setAllSubjects(data.subjects);
        }
      } catch (error) {
        console.error("❌ 科目情報の取得エラー:", error);
      }
    };
    fetchAllSubjects();
  }, []);

  // 担当科目取得 useEffect：講師の場合のみAPI呼び出し、管理者の場合はinitialDataから設定
  useEffect(() => {
    if (isAdminEditing) {
      // 管理者の場合は、initialDataにsubjectが1件あるので配列にして設定
      if (initialData?.subject) {
        setSubjects([initialData.subject]);
      }
      setFormData((prevData) => {
        return {
          ...prevData,
          [studentid]: initialData ? { ...initialData } : {},
        };
      });
      setLoading(false);
      return;
    }
    if (!teacherid || !studentid) {
      setLoading(false);
      return;
    }
    const fetchSubjects = async () => {
      try {
        console.log("📡 APIから teacherAssignments を取得開始:", { teacherid, studentid });
        const assignmentData = await getRequest("teacherAssignments");
        if (!assignmentData || !assignmentData.students) {
          console.warn("⚠ teacherAssignments にデータがありません:", teacherid);
          setSubjects([]);
          setLoading(false);
          return;
        }
        const studentInfo = assignmentData.students[studentid];
        if (!studentInfo) {
          console.warn("⚠ teacherAssignments.students に生徒情報がありません:", studentid);
          setSubjects([]);
          setLoading(false);
          return;
        }
        const studentSubjects = studentInfo.subjects || [];
        console.log("✅ subjects データ取得成功:", studentSubjects);
        setSubjects(studentSubjects);

        // ローカルストレージから復元
        const savedData = JSON.parse(sessionStorage.getItem(`lessonReportData_${studentid}`) || "{}");
        setFormData((prevData) => {
          if (!prevData[studentid] && savedData[studentid]) {
            return {
              ...prevData,
              [studentid]: {
                ...getDefaultLessonReport(studentSubjects),
                ...savedData[studentid],
              },
            };
          }
          return {
            ...prevData,
            [studentid]: {
              ...getDefaultLessonReport(studentSubjects),
              ...prevData[studentid],
            },
          };
        });

        setLoading(false);
        setDataLoaded((prev) => ({ ...prev, [studentid]: true }));
      } catch (error) {
        console.error("❌ 科目データの取得エラー:", error);
        setSubjects([]);
        setLoading(false);
      }
    };
    fetchSubjects();
  }, [teacherid, studentid, initialData, isAdminEditing]);

  // 初期日付設定（timestampはISO文字列を想定）：管理者も対象
  useEffect(() => {
    if (isAdminEditing && initialData?.timestamp) {
      const dateJST = new Date(initialData.timestamp_jst).toISOString().split("T")[0];;
      setSelectedDate(dateJST);
    }
  }, [isAdminEditing, initialData]);

  // 過去の授業報告をAPI経由で取得
  useEffect(() => {
    if (studentid && formData[studentid]?.subject && allSubjects.length > 0) {
      fetchPreviousReports();
    } else {
      setPreviousReports([]);
    }
  }, [studentid, formData[studentid]?.subject, allSubjects]);

  const fetchPreviousReports = async () => {
    try {
      const selectedSubject = formData[studentid]?.subject || "";
      const selectedSubjectObj = allSubjects.find((s) => s.name === selectedSubject);
      let subjectQuery;
      if (selectedSubjectObj) {
        subjectQuery = allSubjects
          .filter((s) => s.category === selectedSubjectObj.category)
          .map((s) => s.name);
        console.log("subjectQuery ", subjectQuery);
      } else {
        subjectQuery = [selectedSubject];
      }

      const endpoint = `lessons/previous?studentid=${studentid}&subjects=${subjectQuery.join(",")}`;
      const data = await getRequest(endpoint);
      if (data && data.reports) {
        setPreviousReports(data.reports);
      } else {
        setPreviousReports([]);
      }
    } catch (error) {
      console.error("❌ 過去のレポート取得エラー:", error);
      setPreviousReports([]);
    }
  };

  // ローカルストレージへ書き込み
  useEffect(() => {
    if (dataLoaded[studentid] && formData && Object.keys(formData).length > 0) {
      sessionStorage.setItem(`lessonReportData_${studentid}`, JSON.stringify(formData));
    }
  }, [formData, studentid, dataLoaded]);

  const getDefaultLessonReport = (studentSubjects) => ({
    subject: studentSubjects.length === 1 ? studentSubjects[0] : "",
    homeworkstatus: "",
    material: "",
    unit: "",
    lessonnotes: "",
    schoolprogress: "",
    lessonattitude: "",
    understanding: "",
    homework: "",
    nextlesson: "",
    teachermemo: "",
  });

  const countFullWidthChars = (str) => {
    let count = 0;
    for (const char of str) {
      count += char.match(/[^\x00-\x7F]/) ? 2 : 1;
    }
    return count;
  };

  const truncateToFullWidthLimit = (str, maxBytes) => {
    let count = 0;
    let truncated = "";
    for (const char of str) {
      count += char.match(/[^\x00-\x7F]/) ? 2 : 1;
      if (count > maxBytes) break;
      truncated += char;
    }
    return truncated;
  };

  const handleInputChange = (field, value) => {
    if (field === "lessonnotes") {
      const maxBytes = 300; // 半角300文字（全角150文字）
      if (countFullWidthChars(value) > maxBytes) {
        value = truncateToFullWidthLimit(value, maxBytes);
      }
    }
    setFormData((prevData) => ({
      ...prevData,
      [studentid]: {
        ...prevData[studentid],
        [field]: value,
      },
    }));
  };

  const handleClearField = (field) => {
    setFormData((prevData) => ({
      ...prevData,
      [studentid]: {
        ...prevData[studentid],
        [field]: "",
      },
    }));
  };

  const validateForm = () => {
    const requiredFields = [
      "subject",
      "homeworkstatus",
      "material",
      "unit",
      "lessonnotes",
      "schoolprogress",
      "lessonattitude",
      "understanding",
      "homework",
      "nextlesson",
    ];
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[studentid]?.[field]) {
        newErrors[field] = "必須項目です";
      }
    });
    console.log("🎨 フォームデータ `formData`:", formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateReportId = (studentid, teacherid) => {
    const now = new Date();
    const ymd = now.toISOString().slice(0, 10).replace(/-/g, "");
    const hms = now.toTimeString().slice(0, 8).replace(/:/g, "");
    return `${ymd}_${studentid}_${teacherid}_${hms}`;
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    const subject = formData[studentid]?.subject;
    if (!subject) {
      toast.error("❌ 科目が選択されていません！", { autoClose: 3000 });
      setIsSaving(false);
      return;
    }
    if (!validateForm()) {
      toast.error("❌ 必須項目をすべて入力してください！", { autoClose: 3000 });
      setIsSaving(false);
      return;
    }
    try {
      let updatedTimestamp = new Date().toISOString();
      if (isAdminEditing && initialData?.timestamp) {
        const originalDateJST = new Date(initialData.timestamp_jst).toISOString().split("T")[0];;
        if (selectedDate === originalDateJST) {
          updatedTimestamp = initialData.timestamp;
        } else {
          updatedTimestamp = new Date(selectedDate + "T00:00:00Z").toISOString();
        }
      }

      let gradeValue;

      if (isAdminEditing) {
        // 管理者なら、フォーム入力された grade を使用
        gradeValue = formData[studentid]?.grade;
      } else {
        // 管理者でない場合、生徒情報から grade を取得
        try {
          const studentData = await getRequest(`students/${studentid}`);
          if (studentData && studentData.student.grade) {
            gradeValue = studentData.student.grade;
          } else {
            throw new Error("生徒の学年情報が取得できませんでした。");
          }
        } catch (error) {
          console.error("❌ 学年取得エラー:", error);
          toast.error("学年情報の取得に失敗しました。");
          setIsSaving(false);
          return;
        }
      }

      const updatedData = {
        ...formData[studentid],
        timestamp: updatedTimestamp,
        grade: gradeValue,
      };

      // DBに保存しないデータ削除
      const doAISuggest = updatedData.doAISuggest;
      delete updatedData.doAISuggest;

      if (isAdminEditing && formData[studentid]?.id) {
        // 更新：PUT /api/lessons/:id
        const response = await putRequest(`lessons/${formData[studentid].id}`, {
          ...updatedData,
        });
        const result = await response.json();
        if (response.ok) {

          if (doAISuggest) {
            // 講師側はAI提案実行しないので、条件分岐
            await handleAIClick();
          } else {
            // onSaveを使用するのは管理者のみ
            if (onSave) {
              onSave(updatedData);
            }
          }

          toast.success("授業報告を更新しました！");

        } else {
          toast.error("授業報告の更新に失敗しました。");
        }
      } else if (!isAdminEditing) {
        // 新規作成：POST /api/lessons
        const newId = generateReportId(studentid, teacherid);
        const payload = {
          id: newId,
          studentid,
          teacherid,
          ...updatedData,
          adminchecked: false,
          // schoolidはサーバー側でJWTから取得するため送信不要
        };
        const response = await postRequest("lessons", {
          ...payload,
        });
        const result = await response.json();
        if (response.ok) {
          setFormData((prevData) => ({
            ...prevData,
            [studentid]: { ...prevData[studentid] },
          }));
          toast.success("授業報告を保存しました！");

          // Dashboard 側へ「保存完了」の通知
          if (onSave) {
            onSave({
              studentid,
              subject: updatedData.subject,
            });
          }
        } else {
          toast.error("授業報告の保存に失敗しました。");
        }
      }
      setRefreshReports((prev) => !prev);
    } catch (error) {
      toast.error("❌ 保存に失敗しました。", { autoClose: 3000 });
      console.error("❌ 授業報告の保存エラー:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFocus = (field, value) => {
    if (value === "") {
      setShowSuggestions((prev) => ({ ...prev, [field]: true }));
    }
  };

  const handleBlur = (field) => {
    setTimeout(() => {
      setShowSuggestions((prev) => ({ ...prev, [field]: false }));
    }, 100);
  };

  const getUniqueSuggestions = (field) => {
    if (!Array.isArray(previousReports) || previousReports.length === 0) return [];
    return [...new Set(previousReports.filter(report => report && report[field]).map(report => report[field]))];
  };

  const handleAIClick = async () => {
    if (formData[studentid]?.ai_suggested) return;

    setIsCreatingAI(true);
    try {
      const res = await postRequest("suggested_goals", {
        lessonid: formData[studentid]?.id,
        studentid: studentid,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "AI提案API失敗");
      }

      const data = await res.json();
      if (data && data.goal) {
        setStudentGoal(data.goal);
        setIsModalOpen(true);

        setFormData((prevData) => ({
          ...prevData,
          [studentid]: {
            ...prevData[studentid],
            ai_suggested: true,
          },
        }));
      }
    } catch (err) {
      console.error("❌ AI提案エラー:", err);
      toast.error("AI課題提案の取得に失敗しました");
    } finally {
      setIsCreatingAI(false);
    }
  };

  const handleGoToCurriculum = () => {
    if (!studentid) return;
    const subjectName = formData[studentid]?.subject || "";
    let subjectParam = "";

    if (subjectName) {
      const subjObj = allSubjects.find((s) => s.name === subjectName);
      subjectParam = subjObj ? subjObj.id : subjectName;
    }

    const params = new URLSearchParams();
    params.set("student", studentid);
    if (subjectParam) params.set("subject", subjectParam);

    window.location.href = `/curriculums?${params.toString()}`;
  };

  const resolveSubjectId = (subjectName) => {
    if (!subjectName) return "";
    const m = allSubjects?.find?.((s) => s.name === subjectName);
    return m ? m.id : "";
  };

  // 講師名取得（teacher オブジェクトのプロパティ名は要確認）
  const getTeacherName = (teacherid) => {
    if (!teacherid) return "";
    const t = teachers.find((teacher) => teacher.id === teacherid);
    return t ? (t.name || "") : "";
  };

  // スケジュールモーダルを開く
  const openScheduleModal = async () => {
    if (!studentid) {
      toast.error("生徒が未選択です");
      return;
    }
    const subjectName = formData[studentid]?.subject || "";
    const subjectId = resolveSubjectId(subjectName);
    if (!subjectId) {
      toast.error("科目を選択してください");
      return;
    }

    setIsScheduleModalOpen(true);
    setScheduleLoading(true);

    try {
      // 期間は AdminStudentSchedule と同様に shiftEntryWindows から取得
      const windowRes = await getRequest("shiftEntryWindows");
      const windows = windowRes.windows || [];
      if (windows.length === 0) {
        toast.error("スケジュール期間が設定されていません");
        setScheduleItems([]);
        return;
      }

      windows.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      const latest = windows[0];
      const start = latest.start_date;
      const end = latest.end_date;

      const res = await getRequest(
        `studentLessonSchedules?studentid=${studentid}&start=${start}&end=${end}`
      );
      const schedules = res.schedules || [];

      const todayStr = new Date().toISOString().split("T")[0];

      // 本日以降 & 当該科目 だけに絞る
      const filtered = schedules.filter((item) => {
        const dateStr = formatDate(item.date);
        if (dateStr < todayStr) return false;

        const itemSubjectId =
          item.student1id === studentid ? item.subject1id : item.subject2id;

        return itemSubjectId === subjectId;
      });

      filtered.sort((a, b) => {
        const da = formatDate(a.date);
        const db = formatDate(b.date);
        if (da === db) return a.period - b.period;
        return da < db ? -1 : 1;
      });

      setScheduleItems(filtered);
    } catch (e) {
      console.error("スケジュール取得エラー:", e);
      toast.error("スケジュールの取得に失敗しました");
      setScheduleItems([]);
    } finally {
      setScheduleLoading(false);
    }
  };

  const closeScheduleModal = () => setIsScheduleModalOpen(false);


  const openUnitsModal = async () => {
    if (!studentid) {
      toast.error("生徒が未選択です");
      return;
    }
    const subjectName = formData[studentid]?.subject || "";
    const subjectId = resolveSubjectId(subjectName);
    if (!subjectId) {
      toast.error("科目を選択してください");
      return;
    }
    try {
      setUnitsLoading(true);
      setIsUnitsModalOpen(true);

      const res = await getRequest(`student_curriculums?studentid=${studentid}`);
      const rows = Array.isArray(res.curriculums) ? res.curriculums : [];
      const filtered = rows.filter((r) => r.subjectid === subjectId);

      const uniq = Array.from(new Map(filtered.map((r) => [r.unit_name, r])).values())
        .map((r) => ({ id: r.id, unit_name: r.unit_name }))
        .sort((a, b) => a.unit_name.localeCompare(b.unit_name, "ja"));

      setUnitOptions(uniq);
    } catch (e) {
      console.error(e);
      toast.error("単元一覧の取得に失敗しました");
      setUnitOptions([]);
    } finally {
      setUnitsLoading(false);
    }
  };

  const closeUnitsModal = () => setIsUnitsModalOpen(false);
  const handlePickUnit = (unitName) => {
    handleInputChange("unit", unitName);
    setIsUnitsModalOpen(false);
  };

  return loading ? (
    <p className="text-gray-500">データを取得中...</p>
  ) : (
    <div className="p-4 shadow-md rounded-lg overflow-y-auto max-h-[80vh] pb-20" style={{ border: `4px solid ${studentColor || "#ccc"}`, backgroundColor: "#fff" }}>
      <h2 className="text-xl font-bold mb-4" style={{ color: studentColor || "#333" }}>{studentName}</h2>
      {isAdminEditing && (
        <label className="flex items-center space-x-2 mb-4">
          <input
            type="checkbox"
            className="w-5 h-5"
            checked={formData[studentid]?.adminchecked ?? false}
            onChange={() => handleInputChange("adminchecked", !formData[studentid]?.adminchecked)}
          />
          <span className="text-lg">管理者確認済み</span>
        </label>
      )}
      {isAdminEditing && (
        <div className="mb-4 border-b pb-3">
          <label className="block text-lg font-bold flex items-center">
            <FaCalendarAlt className="mr-2 text-blue-500" /> 授業日
          </label>
          <input
            type="date"
            className="w-full p-2 border rounded-lg shadow-sm"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      )}
      {isAdminEditing && (
        <div className="mb-4 border-b pb-3">
          <label className="block text-lg font-bold flex items-center">
            <FaUserGraduate className="mr-2 text-pink-500" /> 学年
          </label>
          <select
            name="grade"
            value={formData[studentid]?.grade || ""}
            onChange={(e) => handleInputChange("grade", e.target.value)}
            className="w-full p-2 border rounded-lg shadow-sm"
          >
            {gradeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}
      <div className="mb-4 border-b pb-3">
        {/* ★科目ラベル＋スケジュール表示ボタン */}
        <div className="flex items-center justify-between">
          <label className="block text-lg font-bold flex items-center">
            <FaBook className="mr-2 text-blue-500" /> 科目
          </label>
          <button
            type="button"
            onClick={openScheduleModal}
            disabled={!formData[studentid]?.subject}
            className={`text-xs underline ${formData[studentid]?.subject
              ? "text-blue-600 hover:text-blue-800"
              : "text-gray-400 cursor-not-allowed"
              }`}
            title={
              formData[studentid]?.subject
                ? "本日以降のスケジュールを表示"
                : "科目を選択すると有効になります"
            }
          >
            スケジュール表示
          </button>
        </div>
        <div className="flex gap-3 mt-2">
          {subjects.map((subj) => (
            <button
              key={subj}
              className={`px-4 py-2 rounded-lg transition shadow-md ${formData[studentid]?.subject === subj
                ? "bg-blue-500 text-white"
                : "bg-gray-200"
                }`}
              onClick={() => handleInputChange("subject", subj)}
            >
              {subj}
            </button>
          ))}
        </div>

      </div>
      <div className="mb-4 border-b pb-3">
        <label className="block text-lg font-bold flex items-center">
          <FaTasks className="mr-2 text-green-500" /> 宿題状況
        </label>
        <div className="flex gap-3">
          {["〇", "△", "×", "忘", "無"].map(option => (
            <button
              key={option}
              className={`px-4 py-2 rounded-lg transition shadow-md ${formData[studentid]?.homeworkstatus === option ? "bg-blue-500 text-white" : "bg-gray-200"}`}
              onClick={() => handleInputChange("homeworkstatus", option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-4 border-b pb-3">
        <div className="flex items-center justify-between">
          <label className="block text-lg font-bold flex items-center">
            <FaBook className="mr-2 text-yellow-500" /> 単元
            <button
              type="button"
              onClick={openUnitsModal}
              className="ml-2 text-blue-600 hover:text-blue-800 text-xs underline"
              title="この生徒と科目の単元一覧を表示"
            >
              単元候補
            </button>
          </label>

          <button
            onClick={handleGoToCurriculum}
            disabled={!formData[studentid]?.subject}
            className={`text-xs underline ${formData[studentid]?.subject
              ? "text-blue-600 hover:text-blue-800"
              : "text-gray-400 cursor-not-allowed"
              }`}
            title={
              formData[studentid]?.subject
                ? "この生徒のカリキュラムを見る"
                : "科目を選択すると有効になります"
            }
            type="button"
          >
            カリキュラム確認・更新
          </button>
        </div>

        <div className="relative mt-2">
          <input
            type="text"
            className="w-full p-2 border rounded-lg shadow-sm pr-10"
            value={formData[studentid]?.unit || ""}
            onChange={(e) => {
              handleInputChange("unit", e.target.value);
              if (e.target.value !== "") {
                setShowSuggestions("unit", false);
              }
            }}
            onFocus={(e) => handleFocus("unit", e.target.value)}
            onBlur={() => handleBlur("unit")}
          />
          {showSuggestions.unit && getUniqueSuggestions("unit").length > 0 && (
            <ul className="absolute top-full left-0 w-full bg-white border rounded-lg shadow-md z-10 max-h-40 overflow-y-auto">
              {getUniqueSuggestions("unit").map((suggestion, index) => (
                <li
                  key={index}
                  className="p-2 hover:bg-gray-200 cursor-pointer"
                  onMouseDown={() => handleInputChange("unit", suggestion)}
                >
                  {suggestion.length > 17 ? `${suggestion.substring(0, 17)}...` : suggestion}
                </li>
              ))}
            </ul>
          )}
          {formData[studentid]?.unit && (
            <button
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              onClick={() => handleClearField("unit")}
              type="button"
              aria-label="単元クリア"
            >
              <FaTimesCircle />
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 border-b pb-3">
        <label className="block text-lg font-bold flex items-center">
          <FaBook className="mr-2 text-purple-500" /> 教材
        </label>
        <div className="relative">
          <input
            type="text"
            className="w-full p-2 border rounded-lg shadow-sm pr-10"
            value={formData[studentid]?.material || ""}
            onChange={(e) => {
              handleInputChange("material", e.target.value);
              if (e.target.value !== "") {
                setShowSuggestions("material", false);
              }
            }}
            onFocus={e => handleFocus("material", e.target.value)}
            onBlur={() => handleBlur("material")}
          />
          {showSuggestions.material && getUniqueSuggestions("material").length > 0 && (
            <ul className="absolute top-full left-0 w-full bg-white border rounded-lg shadow-md z-10 max-h-40 overflow-y-auto">
              {getUniqueSuggestions("material").map((suggestion, index) => (
                <li
                  key={index}
                  className="p-2 hover:bg-gray-200 cursor-pointer"
                  onMouseDown={() => handleInputChange("material", suggestion)}
                >
                  {suggestion.length > 17 ? `${suggestion.substring(0, 17)}...` : suggestion}
                </li>
              ))}
            </ul>
          )}
          {formData[studentid]?.material && (
            <button
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              onClick={() => handleClearField("material")}
            >
              <FaTimesCircle />
            </button>
          )}
        </div>
      </div>
      <div className="mb-4 border-b pb-3">
        <label className="block text-lg font-bold flex items-center">
          <FaChartLine className="mr-2 text-indigo-500" /> 学校進度
        </label>
        <div className="flex gap-3">
          {["早", "同", "遅", "復習"].map(option => (
            <button
              key={option}
              className={`px-4 py-2 rounded-lg transition shadow-md ${formData[studentid]?.schoolprogress === option ? "bg-blue-500 text-white" : "bg-gray-200"}`}
              onClick={() => handleInputChange("schoolprogress", option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-1">
        <label className="block text-lg font-bold flex items-center">
          <FaClipboardList className="mr-2 text-orange-500" /> 授業考察

          <button
            type="button"
            onClick={() => {
              if (!studentid) return;
              // カリキュラム遷移と同じ形式でパラメータを作成
              const params = new URLSearchParams();
              params.set("studentid", studentid);
              window.location.href = `/karte?${params.toString()}`;
            }}
            className="ml-2 text-blue-600 hover:text-blue-800 text-xs underline"
          >
            カルテ確認・更新
          </button>
        </label>
        <textarea
          className="w-full p-2 border rounded-lg shadow-sm h-24"
          value={formData[studentid]?.lessonnotes || ""}
          onChange={(e) => handleInputChange("lessonnotes", e.target.value)}
          maxLength={300}
          placeholder="授業考察を入力してください（全角150文字まで）"
        />
      </div>
      {isAdminEditing && (
        <div className="mb-4 border-b pb-3">
          {!formData[studentid]?.ai_suggested ? (
            <label className="flex items-center space-x-2 mb-4">
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={formData[studentid]?.doAISuggest ?? false}
                onChange={() => handleInputChange("doAISuggest", !formData[studentid]?.doAISuggest)}
              />
              <span className="text-lg">保存後にAI課題提案を実行する</span>
            </label>
          ) : (
            <span className="text-green-500 font-semibold">AI課題提案済み</span>
          )}
        </div>
      )}

      <div className="mb-4 border-b pb-3">
        <label className="block text-lg font-bold flex items-center">
          <FaChalkboardTeacher className="mr-2 text-teal-500" /> 授業態度
        </label>
        <div className="flex gap-3">
          {["A", "B", "C", "D", "E"].map(option => (
            <button
              key={option}
              className={`px-4 py-2 rounded-lg transition shadow-md ${formData[studentid]?.lessonattitude === option ? "bg-blue-500 text-white" : "bg-gray-200"}`}
              onClick={() => handleInputChange("lessonattitude", option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-4 border-b pb-3">
        <label className="block text-lg font-bold flex items-center">
          <FaChartLine className="mr-2 text-blue-500" /> 理解度
        </label>
        <div className="flex gap-3">
          {["A", "B", "C", "D", "E"].map(option => (
            <button
              key={option}
              className={`px-4 py-2 rounded-lg transition shadow-md ${formData[studentid]?.understanding === option ? "bg-blue-500 text-white" : "bg-gray-200"}`}
              onClick={() => handleInputChange("understanding", option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <HomeworkInput
        studentid={studentid}
        formData={formData}
        handleInputChange={handleInputChange}
        inputRefs={inputRefs}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        handleFocus={handleFocus}
        handleBlur={handleBlur}
        getUniqueSuggestions={getUniqueSuggestions}
      />
      <div className="mb-4 border-b pb-3">
        <label className="block text-lg font-bold flex items-center">
          <FaUserGraduate className="mr-2 text-pink-500" /> 次回範囲
        </label>
        <div className="relative">
          <input
            type="text"
            className="w-full p-2 border rounded-lg shadow-sm pr-10"
            value={formData[studentid]?.nextlesson || ""}
            onChange={(e) => {
              handleInputChange("nextlesson", e.target.value);
              if (e.target.value !== "") {
                setShowSuggestions("nextlesson", false);
              }
            }}
            onFocus={e => handleFocus("nextlesson", e.target.value)}
            onBlur={() => handleBlur("nextlesson")}
          />
          {showSuggestions.nextlesson && getUniqueSuggestions("nextlesson").length > 0 && (
            <ul className="absolute top-full left-0 w-full bg-white border rounded-lg shadow-md z-10 max-h-40 overflow-y-auto">
              {getUniqueSuggestions("nextlesson").map((suggestion, index) => (
                <li
                  key={index}
                  className="p-2 hover:bg-gray-200 cursor-pointer"
                  onMouseDown={() => handleInputChange("nextlesson", suggestion)}
                >
                  {suggestion.length > 17 ? `${suggestion.substring(0, 17)}...` : suggestion}
                </li>
              ))}
            </ul>
          )}
          {formData[studentid]?.nextlesson && (
            <button
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              onClick={() => handleClearField("nextlesson")}
            >
              <FaTimesCircle />
            </button>
          )}
        </div>
      </div>
      <div className="mb-4 border-b pb-3">
        <label className="block text-lg font-bold flex items-center">
          <FaRegStickyNote className="mr-2 text-gray-500" /> 講師メモ
        </label>
        <textarea
          className="w-full p-2 border rounded-lg shadow-sm h-24"
          value={formData[studentid]?.teachermemo || ""}
          onChange={(e) => handleInputChange("teachermemo", e.target.value)}
        />
      </div>
      <div className={`fixed left-0 w-full lg:left-64 lg:w-[calc(100%-16rem)] ${isAdminEditing ? "bottom-0" : "bottom-[60px]"} bg-blue-500 text-white shadow-md z-90`}>
        <button
          disabled={isSaving}
          className={`w-full p-4 text-lg font-bold flex items-center justify-center rounded-none ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={handleSave}
        >
          {isSaving ? (
            <>
              <FaSpinner className="animate-spin mr-2" /> 保存中...
            </>
          ) : (
            <>
              <FaCheck className="mr-2" /> {isAdminEditing ? "更新" : "保存"}
            </>
          )}
        </button>
      </div>

      <PreviousLessonReports
        studentid={studentid}
        selectedSubject={formData[studentid]?.subject}
        refresh={refreshReports}
      />

      {isModalOpen && (
        <AddStudentGoalModal
          onClose={() => setIsModalOpen(false)}
          onAdded={() => {
            setIsModalOpen(false);
            setRefreshReports(prev => !prev);
            if (onSave) {
              onSave();
            }
          }}
          studentid={studentid}
          goalData={studentGoal}
        />
      )}

      {isCreatingAI && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-white">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-white border-solid"></div>
            <p className="text-lg font-semibold">AIが課題を作成中です...</p>
          </div>
        </div>
      )}

      {isScheduleModalOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-5 w-full max-w-lg max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center">
                <FaCalendarAlt className="mr-2 text-blue-500" />
                スケジュール（{studentName} / {formData[studentid]?.subject || ""}）
              </h3>
              <button
                type="button"
                onClick={closeScheduleModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            {scheduleLoading ? (
              <p className="text-sm text-gray-500">読み込み中...</p>
            ) : scheduleItems.length === 0 ? (
              <p className="text-sm text-gray-500">
                本日以降のスケジュールは登録されていません。
              </p>
            ) : (
              <table className="w-full text-sm border-t border-b">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-1 px-2 text-left">日付</th>
                    <th className="py-1 px-2 text-center">講</th>
                    <th className="py-1 px-2 text-center">担当講師</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleItems.map((s, idx) => (
                    <tr key={`${s.date}_${s.period}_${idx}`} className="border-t last:border-b">
                      <td className="py-1 px-2">
                        {new Date(s.date).toLocaleDateString("ja-JP", {
                          month: "numeric",
                          day: "numeric",
                          weekday: "short",
                        })}
                      </td>
                      <td className="py-1 px-2 text-center">{s.period}講</td>
                      <td className="py-1 px-2 text-center">
                        {s.teacherid
                          ? getTeacherName(s.teacherid) || "担当講師不明"
                          : "未割り当て"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={closeScheduleModal}
                className="px-3 py-1 rounded border text-sm"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {isUnitsModalOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-5 w-full max-w-md">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">単元を選択</h3>
              <button
                type="button"
                onClick={closeUnitsModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="閉じる"
              >
                ×
              </button>
            </div>

            {unitsLoading ? (
              <p className="text-sm text-gray-500">読み込み中...</p>
            ) : unitOptions.length === 0 ? (
              <p className="text-sm text-gray-500">この科目の単元は登録されていません</p>
            ) : (
              <div className="max-h-80 overflow-auto divide-y">
                {unitOptions.map((u) => (
                  <button
                    key={u.id + u.unit_name}
                    type="button"
                    onClick={() => handlePickUnit(u.unit_name)}
                    className="w-full text-left py-2 px-2 hover:bg-blue-50"
                  >
                    {u.unit_name}
                  </button>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={closeUnitsModal}
                className="px-3 py-1 rounded border text-sm"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LessonReportForm;
